import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Box,
  IconButton,
  Typography,
  useTheme,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ImageColetion } from "../../../../../types";
import { ImageDrawing } from "../image-drawing/image-drawing";
import { Toolbar } from "../image-drawing/toolbar";
import { MdOutlineDraw } from "react-icons/md";
import { CustomMarkdown } from "../../../../custom-markdown/custom-markdown";
import { useAuthenticatedFileUrl } from "../../../../../hooks/useAuthenticatedFileUrl";

interface ImageSwiperModalProps {
  open: boolean;
  onClose: () => void;
  images: ImageColetion[];
  initialIndex?: number;
}

const SwiperSlideImage: React.FC<
  React.ComponentProps<typeof ImageDrawing> & { src: string }
> = ({ src, ...rest }) => {
  const { url: resolvedSrc } = useAuthenticatedFileUrl(src);
  return <ImageDrawing src={resolvedSrc || src} {...rest} />;
};

export const ImageSwiperModal: React.FC<ImageSwiperModalProps> = ({
  open,
  onClose,
  images,
  initialIndex = 0,
}) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isShort, setIsShort] = useState(false);
  const [isToolbarOpen, setIsToolbarOpen] = useState(true);

  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  const [isClear, setIsClear] = useState<boolean[]>(
    new Array(images.length).fill(false)
  );

  const imageRefs = useRef<HTMLDivElement[]>(
    new Array(images.length).fill(null)
  );

  const [isTextOpen, setIsTextOpen] = useState<boolean[]>(
    new Array(images.length).fill(true)
  );

  const handleResize = useCallback(() => {
    if (!imageRefs.current[currentIndex]) {
      return;
    }

    setIsShort(
      imageRefs.current[currentIndex].getBoundingClientRect().width < 500
    );
  }, [currentIndex]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(handleResize, 100);
    return () => window.clearTimeout(timer);
  }, [open, currentIndex, handleResize]);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setControlsVisible(true);
      setIsToolbarOpen(true);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!controlsVisible) return;

    const timer = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [controlsVisible]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setControlsVisible(true);
  };
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setControlsVisible(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (images.length > 1) {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    }
    if (e.key === "Escape") onClose();
    setControlsVisible(true);
  };

  const showSlideNav = images.length > 1;

  if (!images.length) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0, 0, 0, 0.95)",
            userSelect: "none",
            outline: "none",
            overflow: "hidden",
            zIndex: theme.zIndex.modal,
          }}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          onMouseMove={() => {
            if (!controlsVisible) {
              setControlsVisible(true);
            }
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "visible",
              }}
            >
              {images.map((img, idx) => (
                <SwiperSlideImage
                  ref={(el) => {
                    if (el instanceof HTMLDivElement) {
                      imageRefs.current[idx] = el;
                    }
                  }}
                  key={idx}
                  src={img.url}
                  alt={img.description || "Image"}
                  text={isShort ? null : img.description}
                  isTextOpen={isShort ? undefined : isTextOpen[currentIndex]}
                  setIsTextOpen={
                    isShort
                      ? undefined
                      : (value) => {
                          setIsTextOpen((old) => {
                            const updated = [...old];
                            updated[idx] = value;
                            return updated;
                          });
                        }
                  }
                  selectedColor={selectedColor}
                  setSelectedColor={setSelectedColor}
                  selectedToolId={selectedToolId}
                  setSelectedToolId={setSelectedToolId}
                  isClear={isClear[idx]}
                  setIsClear={(value) => {
                    setIsClear((old) => {
                      const updated = [...old];
                      updated[idx] = value;
                      return updated;
                    });
                  }}
                  sx={{
                    position: "absolute",
                    objectFit: "contain",
                    visibility: idx === currentIndex ? "visible" : "hidden",
                    pointerEvents: idx === currentIndex ? "auto" : "none",
                    zIndex: idx === currentIndex ? 2 : 0,
                    transition: "opacity 0.3s ease",
                    cursor: "pointer",
                    overflow: "visible",
                  }}
                />
              ))}

              {isToolbarOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: (theme) =>
                      `max(${theme.spacing(2)}, env(safe-area-inset-bottom, 0px))`,
                    transform: "translateX(-50%)",
                    zIndex: 35,
                    pointerEvents: "auto",
                    maxWidth: "calc(100% - 16px)",
                  }}
                >
                  <Toolbar
                    selectedToolId={selectedToolId}
                    selectedColor={selectedColor}
                    setSelectedToolId={setSelectedToolId}
                    setSelectedColor={setSelectedColor}
                    onClear={() =>
                      setIsClear((old) => {
                        const updated = [...old];
                        updated[currentIndex] = true;
                        return updated;
                      })
                    }
                    onHide={() => setIsToolbarOpen(false)}
                  />
                </Box>
              )}

              {!isToolbarOpen && (
                <IconButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsToolbarOpen(true);
                  }}
                  aria-label="Show drawing tools"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: (theme) =>
                      `max(${theme.spacing(2)}, env(safe-area-inset-bottom, 0px))`,
                    transform: "translateX(-50%)",
                    zIndex: 35,
                    width: "3rem",
                    height: "3rem",
                    padding: "0",
                    borderRadius: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#ddd",
                    opacity: "0.85",
                    color: "#F20000",
                    pointerEvents: "auto",
                    "&:hover": {
                      opacity: 1,
                      backgroundColor: "#ddd",
                    },
                  }}
                >
                  <MdOutlineDraw />
                </IconButton>
              )}
            </Box>
          </Box>

          <IconButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 40,
              color: "white",
              bgcolor: "rgba(0,0,0,0.4)",
              pointerEvents: "auto",
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Fade in={controlsVisible}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 25,
                // Do not block toolbar interactions while nav controls are visible.
                pointerEvents: "none",
              }}
            >
              {showSlideNav && (
                <IconButton
                  type="button"
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "white",
                    bgcolor: "rgba(0,0,0,0.4)",
                    pointerEvents: "auto",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
              )}

              {showSlideNav && (
                <IconButton
                  type="button"
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "white",
                    bgcolor: "rgba(0,0,0,0.4)",
                    pointerEvents: "auto",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              )}

              {(showSlideNav ||
                (isShort && !!images[currentIndex].description)) && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "white",
                    bgcolor: "rgba(0,0,0,0.4)",
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    [theme.breakpoints.down("sm")]: {
                      display: "none",
                    },
                  }}
                >
                  {showSlideNav && (
                    <Typography variant="body2">
                      {currentIndex + 1} / {images.length}
                    </Typography>
                  )}

                  {isShort && images[currentIndex].description && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 56,
                        left: "15%",
                        transform: "translateX(-35%)",
                        maxWidth: "100%",
                        minWidth: "200px",
                        maxHeight: "150px",
                        overflowY: "auto",
                        color: "white",
                        bgcolor: "rgba(0,0,0,0.4)",
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        textAlign: "left",
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(255,255,255,0.5)",
                          borderRadius: "3px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "rgba(0,0,0,0.2)",
                        },
                      }}
                    >
                      <Typography variant="body2">
                        <CustomMarkdown>
                          {images[currentIndex].description}
                        </CustomMarkdown>
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Fade>
        </Box>
      </Fade>
    </Modal>
  );
};
